import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, User, Calendar, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const articles = {
  'hire-pitch-deck-professional': {
    title: "Hire a Pitch Deck Professional or Make Your Own? What's Best for Your Startup",
    category: 'Pitch Decks',
    author: 'Arshdeep Singh',
    date: 'May 25, 2026',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1200&h=600&fit=crop',
    intro:
      'Your pitch deck is often the first serious introduction investors have to your company. The question is whether you should build it yourself or bring in a professional.',
    sections: [
      {
        heading: 'Why Your Pitch Deck Matters',
        paragraphs: [
          'A pitch deck is more than a presentation. It is a concise representation of your business, your market opportunity, your traction, your strategy and the team behind the company.',
          'Investors often review dozens of opportunities before deciding which founders deserve a meeting. A clear and compelling deck can help you communicate your opportunity quickly and make the next conversation easier.'
        ]
      },
      {
        heading: 'When DIY Makes Sense',
        paragraphs: [
          'Creating your own deck can make sense when you are still refining your business model, validating your positioning or preparing for your first investor conversations.',
          'Building the deck yourself forces you to think carefully about your story, numbers and value proposition. That understanding becomes extremely useful when you eventually speak with investors.'
        ]
      },
      {
        heading: 'When You Should Hire a Professional',
        paragraphs: [
          'A professional can be valuable when you already understand your business but struggle to translate that knowledge into a compelling investor narrative.',
          'Professional support can improve structure, visual hierarchy, financial storytelling and overall investor readability. The objective should not be to make the deck look beautiful simply for the sake of design. Every slide should help communicate the investment opportunity.'
        ]
      },
      {
        heading: 'The Hidden Cost of Getting It Wrong',
        paragraphs: [
          'The biggest cost of a weak pitch deck is not the cost of redesigning the slides. It can be the investor conversations you never get because the opportunity was not communicated clearly.',
          'A strong deck should make it easy for an investor to understand what you do, why the market matters, why your company can win and what you are asking for.'
        ]
      },
      {
        heading: 'The Bottom Line',
        paragraphs: [
          'There is no universal answer. Early-stage founders can absolutely create effective decks themselves. As the fundraising becomes more significant, professional support can become increasingly valuable.',
          'The best pitch deck is not necessarily the most visually impressive one. It is the one that communicates the investment opportunity clearly, credibly and efficiently.'
        ]
      }
    ]
  },

  'startup-fundraising-strategies': {
    title: 'Startup Fundraising Strategies Every Founder Should Know',
    category: 'Strategy',
    author: 'Mohd Sajid',
    date: 'June 2, 2026',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&h=600&fit=crop',
    intro:
      'Successful fundraising is rarely about simply asking investors for money. It is about preparing the company, identifying the right investors and creating a process that builds momentum.',
    sections: [
      {
        heading: 'Start With Fundraising Readiness',
        paragraphs: [
          'Before approaching investors, founders should understand their current stage, capital requirement, milestones and the evidence supporting their growth opportunity.',
          'A founder who knows exactly why capital is required and what the capital will accomplish is much better positioned for investor conversations.'
        ]
      },
      {
        heading: 'Target the Right Investors',
        paragraphs: [
          'Not every investor is appropriate for every startup. Investors differ by cheque size, geography, sector, stage and investment thesis.',
          'Building a focused investor list can improve outreach efficiency and increase the likelihood of meaningful conversations.'
        ]
      },
      {
        heading: 'Build Momentum',
        paragraphs: [
          'Fundraising works best when conversations are coordinated rather than spread randomly over many months.',
          'Creating momentum allows founders to use investor interest, meetings and progress to generate additional credibility throughout the process.'
        ]
      },
      {
        heading: 'Tell a Clear Investment Story',
        paragraphs: [
          'Investors need to understand the problem, solution, market, traction, business model, competitive advantage and team.',
          'The story should connect these elements rather than presenting them as disconnected slides.'
        ]
      },
      {
        heading: 'Keep Improving',
        paragraphs: [
          'Every investor conversation can provide useful feedback. Track objections, recurring questions and areas of confusion.',
          'The objective is not to change the company based on every investor opinion. It is to identify patterns that can improve your fundraising communication.'
        ]
      }
    ]
  },

  'investor-ready-financial-models': {
    title: 'How to Build Investor-Ready Financial Models',
    category: 'Finance',
    author: 'Arshdeep Singh',
    date: 'June 10, 2026',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop',
    intro:
      'A financial model should help investors understand how your business works, what drives growth and how additional capital can change the trajectory.',
    sections: [
      {
        heading: 'Start With Business Drivers',
        paragraphs: [
          'A useful financial model begins with operational assumptions rather than arbitrary revenue numbers.',
          'Identify the drivers that actually create revenue and costs in your business. These might include customers, transactions, average order value, pricing, conversion rates, headcount or capacity.'
        ]
      },
      {
        heading: 'Separate Assumptions From Results',
        paragraphs: [
          'Investors should be able to see which numbers are assumptions and which numbers are calculated outputs.',
          'Keeping assumptions organized makes the model easier to review and easier to update as the business develops.'
        ]
      },
      {
        heading: 'Model Multiple Scenarios',
        paragraphs: [
          'A single forecast can create a false sense of certainty. Base, upside and downside scenarios can provide a more useful picture of potential outcomes.',
          'Scenario planning also helps founders understand which variables have the biggest impact on the business.'
        ]
      },
      {
        heading: 'Connect Funding to Milestones',
        paragraphs: [
          'The amount you are raising should have a logical connection to the milestones you expect the capital to achieve.',
          'Investors want to understand not only how much money you need, but what the business should look like after deploying that capital.'
        ]
      }
    ]
  },

  'pre-seed-seed-series-a': {
    title: 'Pre-Seed vs Seed vs Series A: Understanding Your Funding Stage',
    category: 'Fundamentals',
    author: 'Mohd Sajid',
    date: 'June 18, 2026',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1200&h=600&fit=crop',
    intro:
      'Understanding funding stages helps founders identify the type of capital, investor and evidence they should be targeting at different points in their company journey.',
    sections: [
      {
        heading: 'Pre-Seed',
        paragraphs: [
          'Pre-seed capital is generally associated with very early company building. Founders may be validating a problem, developing a product or establishing initial market evidence.',
          'At this stage, investors are often evaluating the founders, the problem, the market opportunity and early evidence of product-market potential.'
        ]
      },
      {
        heading: 'Seed',
        paragraphs: [
          'Seed-stage companies generally have more evidence than an idea-stage company and are working toward repeatable growth.',
          'The focus may include product-market fit, early revenue, customer growth, retention and a clearer go-to-market strategy.'
        ]
      },
      {
        heading: 'Series A',
        paragraphs: [
          'Series A companies typically need to demonstrate stronger evidence that the business model can scale.',
          'Investors may look closely at growth metrics, unit economics, market size, competitive positioning and the scalability of the organization.'
        ]
      },
      {
        heading: 'Choosing the Right Stage',
        paragraphs: [
          'The important question is not simply what funding round sounds impressive. It is whether the company has the evidence and maturity appropriate for that stage.',
          'Raising the right amount from the right investor at the right time can create a much stronger foundation for the next phase of growth.'
        ]
      }
    ]
  },

  'cold-email-to-investors': {
    title: 'The Art of the Cold Email to Investors',
    category: 'Outreach',
    author: 'Arshdeep Singh',
    date: 'June 25, 2026',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1596524430615-b46475ddff6e?w=1200&h=600&fit=crop',
    intro:
      'A cold investor email has one primary objective: earn enough attention to start a conversation. It does not need to tell the entire company story.',
    sections: [
      {
        heading: 'Keep It Short',
        paragraphs: [
          'Investors receive a large number of emails. A concise message makes it easier to understand the opportunity quickly.',
          'Focus on the company, the problem, traction or evidence, the fundraising requirement and why you are contacting that particular investor.'
        ]
      },
      {
        heading: 'Personalize the Message',
        paragraphs: [
          'Generic mass emails are easy to ignore. A short sentence explaining why the investor is relevant can make the outreach considerably more meaningful.',
          'Look for genuine alignment in sector, stage, geography, portfolio or investment thesis.'
        ]
      },
      {
        heading: 'Lead With Evidence',
        paragraphs: [
          'Whenever possible, communicate concrete evidence rather than broad claims.',
          'Revenue, customer growth, partnerships, retention, product usage or other meaningful milestones can help establish credibility.'
        ]
      },
      {
        heading: 'Have a Clear Call to Action',
        paragraphs: [
          'Do not make the investor guess what you want. Ask for a short introductory conversation or permission to share the deck.',
          'A simple next step reduces friction and makes it easier for the investor to respond.'
        ]
      }
    ]
  },

  'negotiate-term-sheets': {
    title: 'How to Negotiate Term Sheets Without Lawyers (At First)',
    category: 'Legal',
    author: 'Mohd Sajid',
    date: 'July 2, 2026',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
    intro:
      'Founders do not need to become lawyers to understand the commercial concepts inside a term sheet. Understanding the basics can make early negotiations much more productive.',
    sections: [
      {
        heading: 'Understand the Economics',
        paragraphs: [
          'Valuation, investment amount and ownership are among the most visible commercial terms, but they should be considered together rather than individually.',
          'Founders should understand how the proposed investment affects ownership before agreeing to headline numbers.'
        ]
      },
      {
        heading: 'Understand Control',
        paragraphs: [
          'Some terms influence who has decision-making power and how important company decisions are approved.',
          'Board rights, voting rights and protective provisions deserve careful attention during negotiations.'
        ]
      },
      {
        heading: 'Understand Investor Protections',
        paragraphs: [
          'Liquidation preferences, anti-dilution provisions and other investor protections can materially affect future outcomes.',
          'These terms can become especially important in downside scenarios or future financing rounds.'
        ]
      },
      {
        heading: 'Bring in Legal Counsel',
        paragraphs: [
          'Commercial understanding should come before legal documentation, but founders should involve qualified legal counsel before signing binding investment documents.',
          'The purpose of understanding the term sheet yourself is to make better commercial decisions, not to replace professional legal advice.'
        ]
      }
    ]
  },

  'building-your-data-room': {
    title: 'Building Your Data Room: A Step-by-Step Guide',
    category: 'Documentation',
    author: 'Arshdeep Singh',
    date: 'July 10, 2026',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=600&fit=crop',
    intro:
      'An organized data room helps investors conduct due diligence efficiently and signals that the company is prepared for serious fundraising conversations.',
    sections: [
      {
        heading: 'Start With the Essentials',
        paragraphs: [
          'A data room should contain the core information an investor may need to evaluate the company.',
          'Typical areas include corporate documents, financial information, cap table, customer information, intellectual property and key commercial agreements.'
        ]
      },
      {
        heading: 'Organize Everything Clearly',
        paragraphs: [
          'Investors should not have to search through dozens of poorly named files to find important information.',
          'Use logical folders, consistent file names and clear version control.'
        ]
      },
      {
        heading: 'Keep Financial Information Current',
        paragraphs: [
          'Financial statements, management accounts, forecasts and supporting schedules should be consistent with the numbers presented in your pitch deck.',
          'Inconsistencies can create unnecessary questions during due diligence.'
        ]
      },
      {
        heading: 'Control Access',
        paragraphs: [
          'Sensitive information should be shared deliberately. Use appropriate access controls and make sure only relevant parties can view confidential documents.',
          'As the fundraising progresses, the data room can become increasingly detailed.'
        ]
      },
      {
        heading: 'Treat the Data Room as a Living System',
        paragraphs: [
          'A good data room is not something you build once and forget. Keep important documents updated as the company evolves.',
          'Being continuously prepared can significantly reduce stress when a serious investor begins due diligence.'
        ]
      }
    ]
  }
};

const Article = () => {
  const { slug } = useParams();
  const article = articles[slug];

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The article you are looking for could not be found.
          </p>
          <Link to="/resources">
            <Button className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Resources
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Article Hero */}
      <section className="relative bg-gradient-to-br from-emerald-700 via-green-700 to-emerald-800 py-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/resources"
              className="inline-flex items-center text-green-100 hover:text-white mb-8 font-semibold transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Resources
            </Link>

            <span className="inline-block px-4 py-2 bg-white/15 text-green-50 rounded-full text-sm font-bold uppercase tracking-wider mb-6">
              {article.category}
            </span>

            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-8">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-green-50">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span>{article.author}</span>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{article.date}</span>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Image */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-[300px] md:h-[500px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-10 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-medium mb-12">
              {article.intro}
            </p>

            {article.sections.map((section, index) => (
              <section key={index} className="mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                  {section.heading}
                </h2>

                {section.paragraphs.map((paragraph, paragraphIndex) => (
                  <p
                    key={paragraphIndex}
                    className="text-lg text-gray-600 leading-relaxed mb-5"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            {/* CTA */}
            <div className="mt-14 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100">
              <Mail className="h-10 w-10 text-emerald-600 mb-5" />

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Need Help With Your Fundraising?
              </h2>

              <p className="text-gray-600 text-lg mb-6">
                If you're preparing to raise capital and want professional
                support, connect with FounderScale Capital.
              </p>

              <Link to="/contact">
                <Button className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold px-6 py-3">
                  Connect With Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Back Button */}
            <div className="mt-12 text-center">
              <Link to="/resources">
                <Button
                  variant="outline"
                  className="border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  More Resources
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default Article;
